import { cloneElement, isValidElement, useCallback, useEffect, useMemo, useRef, useState, type ReactElement, type ReactNode, type RefObject } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, Bookmark, Columns3, Pencil, Search, Trash2 } from 'lucide-react';
import { TETO_BUSCA, type Pagina } from '@malgoos/shared';
import { del, get, patch, post } from '../nucleo/api';
import { discretoAtivo } from '../nucleo/discreto';
import { censurarTexto, nomePessoa } from '../nucleo/formato';
import { anunciarOcultos } from './sensivel';
import { Botao } from './base';
import { Carregando, Erro, Vazio } from './estados';
import { buscaDaUrl, comBuscaNaUrl, filtrosDaVisao, filtrosDaVisaoPadrao, filtrosParaVisao, mensagemDoErroDaVisao, visaoCabe, type FiltrosTabela } from './filtros-visao';
import { Entrada, useCamposPersonalizados, valorExtra } from './formulario';
import { Popover } from './menu';
import { textoDoNo } from './texto-cortado';
import { Modal } from './sobreposicao';
import { CamposCompartilharVisao, corpoCompartilhar, PARAMETRO_TELA_DA_VISAO, PARAMETRO_VISAO_SALVA, SEM_COMPARTILHAR, usePodeCompartilharVisao, type CompartilhamentoVisao, type ValorCompartilhar } from './compartilhar-visao';
import { BlocoVirtual, blocosPorPagina, perto, totalDeLinhasAria, useBlocoComFoco, useDesktop, useImprimindo, useLargurasEstaveis, useMediaDeLinha } from './tabela-virtual';
import { useToast } from './toast';
import { carregarMais, itensVisiveisDaTabela } from '../nucleo/foco';

export interface Coluna<T> {
  chave: string;
  rotulo: string;
  render?: (linha: T) => ReactNode;
  /** Envia `ordem=<chave>` e `direcao` para a API. */
  ordenavel?: boolean;
  alinhar?: 'direita';
  mono?: boolean;
  /** Escondida por padrão (pode ser ligada em Colunas). */
  oculta?: boolean;
  /** Mostrada como título do cartão no celular. */
  principal?: boolean;
  /**
   * RCRM5 VIS-B2: ação da linha (um botão) que no celular fica ao lado do cartão, irmã do botão dele (nunca dentro), em vez
   * de disputar as 3 colunas do cartão. Aditivo: sem a opção, nada muda.
   */
  acaoNoCartao?: boolean;
}

/**
 * Fase 2 (W14): seleção de linhas para ação em lote (baixa em lote do Contas). Opcional: sem ela, a tabela não muda. A
 * caixa fica numa coluna própria no desktop e ao lado do cartão no celular (irmã do botão do cartão, nunca dentro dele).
 */
export interface SelecaoTabela<T> {
  marcados: ReadonlySet<string>;
  aoAlternar: (linha: T, marcado: boolean) => void;
  /** Marca ou desmarca as linhas carregadas que podem ser escolhidas (caixa do cabeçalho). */
  aoAlternarTodas?: (linhas: T[], marcado: boolean) => void;
  /** Linha que pode ser escolhida (padrão: todas). */
  pode?: (linha: T) => boolean;
  /** Nome da caixa da linha para o leitor de tela ("Selecionar F-0012"). */
  rotulo: (linha: T) => string;
  rotuloTodas?: string;
}

/**
 * Caixa de seleção da linha. Review r16 visual M5: o quadrado continua de 16px, mas a área clicável é o rótulo de 24x24 em
 * volta (WCAG 2.5.8): o clique perto da caixa também marca, e os 4px de folga de cada lado afastam o vizinho (o botão do
 * cartão no celular) do alvo, como o `target-size` do axe pede. O clique no rótulo não abre a linha.
 */
/**
 * Review r43 robustez (M2): a página pode vir vazia com cursor (a Auditoria do Administrador restrito filtra por empresa
 * depois de ler, e um trecho longo de outra empresa esgota o teto de varredura da requisição). Com mais no servidor, a
 * lista segue no ramo normal, com o "Carregar mais"; o estado vazio é só para quando não há mais nada.
 */
export function mostraEstadoVazio(linhas: number, temMais: boolean) {
  return linhas === 0 && !temMais;
}

/**
 * Review r35 visual (M2): a 1280 a tabela larga rola na horizontal e, sem nada focável dentro, quem usa teclado não
 * alcança as colunas cortadas (axe `scrollable-region-focusable`). Só nesse caso o contêiner entra na ordem do Tab.
 */
const FOCAVEIS = 'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])';
export function precisaFocoDeRolagem(el: Pick<HTMLElement, 'scrollWidth' | 'clientWidth' | 'querySelector'> | null) {
  return !!el && el.scrollWidth > el.clientWidth + 1 && !el.querySelector(FOCAVEIS);
}
function useRolagemSemFoco(ref: RefObject<HTMLDivElement | null>, chave: string) {
  const [rolavel, setRolavel] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const medir = () => setRolavel(precisaFocoDeRolagem(el));
    medir();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => ro.disconnect();
  }, [ref, chave]);
  return rolavel;
}

function CaixaSelecao({ marcado, rotulo, aoMudar, desabilitado, indeterminado }: { marcado: boolean; rotulo: string; aoMudar: (v: boolean) => void; desabilitado?: boolean; indeterminado?: boolean }) {
  return (
    <label data-alvo-selecao className={`flex size-6 shrink-0 items-center justify-center ${desabilitado ? 'cursor-not-allowed' : 'cursor-pointer'}`} onClick={(e) => e.stopPropagation()}>
      <input
        type="checkbox"
        data-selecao-linha
        className="size-4 cursor-pointer accent-acento-forte disabled:cursor-not-allowed"
        checked={marcado}
        disabled={desabilitado}
        aria-label={rotulo}
        ref={(el) => {
          if (el) el.indeterminate = !!indeterminado;
        }}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => aoMudar(e.target.checked)}
      />
    </label>
  );
}

interface Visao {
  id: string;
  nome: string;
  filtros: Record<string, unknown>;
  colunas: string[];
  ordenacao: { ordem?: string; direcao?: 'asc' | 'desc' };
  padrao: boolean;
  /** Review r26 robustez (M2): visão gravada antes do teto; a API a devolve sem os filtros e a tela não a aplica. */
  acima_do_teto?: boolean;
  /**
   * E1 (R37-SPEC-M2): a visão pode ser de outra pessoa, compartilhada com quem abre (vem depois das próprias, com o nome de
   * quem salvou); só a própria se edita e se remove. Ausentes (API antiga): a visão é da pessoa.
   */
  meu?: boolean;
  autor?: string | null;
  compartilhamento?: CompartilhamentoVisao;
  papeis?: string[];
  pessoas?: string[];
  em_relatorios?: boolean;
}

/**
 * Botão que abre o registro da linha da tabela pelo teclado (r6 visual A2: `<tr onClick>` não recebe foco). Fica na célula
 * principal, ocupa a largura dela e não muda o visual; o foco aparece pelo `:focus-visible` global. Use também em tabelas próprias.
 */
/**
 * Coluna sem `render`: o valor como veio; nome de pessoa ou empresa dos dados do negócio segue a opção de nomes do modo
 * discreto. O responsável é usuário do sistema e fica com o nome (decisão r9; r11 visual B4).
 */
const NOME_PESSOA = /(^|_)(parceiro|fornecedor|funcionario|cliente)_nome$/;
function valorCru(l: object, chave: string) {
  const v = String((l as Record<string, unknown>)[chave] ?? '');
  if (NOME_PESSOA.test(chave)) return nomePessoa(v);
  // Texto sem formatador próprio (descrição, título, observação, código): os padrões sensíveis somem no modo discreto (r11).
  return censurarTexto(v);
}

/**
 * Review r19 spec (M1): coluna COM `render` também passa pela censura no modo discreto. A coluna "Destino" de Contas >
 * Cobranças devolvia o e-mail do cliente cru e só a coluna sem `render` era censurada (`valorCru`). Agora o texto que o
 * `render` devolve (e o dos filhos dos elementos, e o `title`) passa por `censurarTexto`: e-mail, telefone, documento, CEP e
 * valor reconhecíveis somem mesmo sem o formatador. É a rede de proteção: o formatador próprio (`email`, `telefoneFormatado`,
 * `documentoFormatado`, `nomePessoa`) continua sendo o certo, porque mascara do jeito de cada dado e conhece os nomes.
 * Já mascarado ("q•••@m•••.test", "R$ •••••") passa sem mudança. Filho que é função (render prop) fica como está.
 */
export function censurarRender(no: ReactNode): ReactNode {
  if (!discretoAtivo()) return no;
  if (typeof no === 'string') return censurarTexto(no);
  if (Array.isArray(no)) return no.map(censurarRender);
  if (!isValidElement<{ children?: ReactNode; title?: unknown }>(no)) return no;
  const { children, title } = no.props;
  const filhos = typeof children === 'string' || Array.isArray(children) || isValidElement(children);
  const titulo = typeof title === 'string';
  if (!filhos && !titulo) return no;
  const props = titulo ? { title: censurarTexto(title) } : undefined;
  const el = no as ReactElement<{ children?: ReactNode; title?: unknown }>;
  if (!filhos) return cloneElement(el, props);
  return Array.isArray(children) ? cloneElement(el, props, ...children.map(censurarRender)) : cloneElement(el, props, censurarRender(children));
}

/** Conteúdo da célula: o `render` da coluna (com a rede da censura) ou o valor cru. */
const celula = <T extends object>(c: Coluna<T>, l: T) => (c.render ? censurarRender(c.render(l)) : valorCru(l, c.chave));

/** Entidades cuja linha É a pessoa ou a empresa (o `nome` da linha é nome de pessoa ou de negócio). */
const ENTIDADE_DE_PESSOA = new Set(['cadastros.parceiro', 'pessoas.funcionario']);
/**
 * Review r19 spec: nomes da própria linha para o texto livre dos campos personalizados (como a ficha faz com `linhasExtras`):
 * com a opção de nomes, "Joana Prado: pagar até..." num campo de texto da Joana sai com as iniciais também na Tabela, quando a
 * coluna é ligada. O parceiro, fornecedor, funcionário ou cliente da linha (`*_nome`) vale em toda tabela; o `nome`, a razão
 * social e o nome fantasia, só na lista de cadastros e de funcionários.
 */
export function nomesDaLinha(l: object, entidade?: string): string[] {
  const r = l as Record<string, unknown>;
  const daPessoa = !!entidade && ENTIDADE_DE_PESSOA.has(entidade);
  return Object.entries(r)
    .filter(([k, v]) => typeof v === 'string' && (NOME_PESSOA.test(k) || (daPessoa && (k === 'nome' || k === 'razao_social' || k === 'nome_fantasia'))))
    .map(([, v]) => v as string);
}

export function BotaoAbrirLinha({ aoAbrir, children }: { aoAbrir: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      data-abrir-linha
      onClick={(e) => {
        e.stopPropagation();
        aoAbrir();
      }}
      className="block w-full min-w-0 cursor-pointer rounded text-left"
    >
      {children}
    </button>
  );
}

export { textoDoNo } from './texto-cortado';

export function useDebounce<T>(v: T, ms = 300) {
  const [d, setD] = useState(v);
  useEffect(() => {
    const t = setTimeout(() => setD(v), ms);
    return () => clearTimeout(t);
  }, [v, ms]);
  return d;
}

/**
 * Review r28 visual (B3): "Remover visão" some depois de remover (a visão ativa sai). O foco que estava nele, ou que já caiu
 * no body, vai ao seletor de visões, que fica na tela; foco em outro lugar (a pessoa seguiu adiante) fica onde está.
 */
export function devolverFocoAoSeletorDeVisoes(o: { ativo: unknown; botao: unknown; corpo: unknown; seletor: { focus: () => void } | null }) {
  if (o.seletor && (o.ativo === o.botao || o.ativo === o.corpo || o.ativo === null)) o.seletor.focus();
}

/**
 * F7: tabela com paginação por cursor, busca, ordenação, visões salvas por usuário e colunas
 * de campos personalizados. No celular vira lista de cartões, sem rolagem horizontal da página.
 */
export function Tabela<T extends { id: string; extras?: Record<string, unknown> }>({
  tela,
  caminho,
  params,
  colunas,
  entidade,
  aoClicar,
  filtrosExtra,
  filtros,
  aoAplicarFiltros,
  vazio,
  chaveConsulta,
  selecao,
  buscaNaUrl = false,
}: {
  tela: string;
  caminho: string;
  params?: Record<string, unknown>;
  colunas: Coluna<T>[];
  entidade?: string;
  aoClicar?: (linha: T) => void;
  filtrosExtra?: ReactNode;
  /** Valores dos filtros extras da tela: entram na visão salva junto com a busca. */
  filtros?: FiltrosTabela;
  /** Restaura os filtros extras ao aplicar uma visão (ou os valores iniciais ao limpar a visão). */
  aoAplicarFiltros?: (filtros: FiltrosTabela) => void;
  vazio?: ReactNode;
  chaveConsulta?: unknown[];
  selecao?: SelecaoTabela<T>;
  /**
   * Review r24 spec (B4): a busca lê o `?busca=` da URL ao abrir (o link dos avisos, contrato da W22) e grava nele a cada
   * mudança (replace). Só nas listas que são a página (não nas tabelas dentro de ficha ou modal, que dividiriam o parâmetro).
   */
  buscaNaUrl?: boolean;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const [url, setUrl] = useSearchParams();
  const [busca, setBuscaLocal] = useState(() => (buscaNaUrl ? buscaDaUrl(url) : ''));
  const setBusca = (v: string) => {
    setBuscaLocal(v);
    // A URL do momento, não a do último render: o filtro que a tela grava no mesmo clique (aplicar visão) não se perde.
    if (buscaNaUrl) setUrl(comBuscaNaUrl(new URLSearchParams(window.location.search), v), { replace: true });
  };
  // Outro link com outra busca, com a lista já aberta (o aviso clicado de novo pelo sino): a busca segue a URL.
  const buscaUrl = buscaNaUrl ? buscaDaUrl(url) : null;
  useEffect(() => {
    if (buscaUrl === null) return;
    setBuscaLocal((b) => ((b.trim() ? b : '') === buscaUrl ? b : buscaUrl));
  }, [buscaUrl]);
  const buscaD = useDebounce(busca);
  const [ordem, setOrdem] = useState<{ ordem?: string; direcao?: 'asc' | 'desc' }>({});
  const extras = useCamposPersonalizados(entidade);
  const todasColunas = useMemo<Coluna<T>[]>(
    () => [
      ...colunas,
      ...extras.map((c) => ({
        chave: c.nome,
        rotulo: c.rotulo,
        oculta: true,
        // Review r12 visual M1: o formatador único dos campos personalizados (data no idioma, texto censurado no modo discreto).
        render: (l: T) => valorExtra(c, l.extras?.[c.nome.slice(7)], ...nomesDaLinha(l, entidade)),
      })),
    ],
    [colunas, extras, entidade],
  );
  const [visiveis, setVisiveis] = useState<string[] | null>(null);
  const colunasMostradas = useMemo(() => todasColunas.filter((c) => (visiveis ? visiveis.includes(c.chave) : !c.oculta)), [todasColunas, visiveis]);

  const visoes = useQuery({ queryKey: ['visoes', tela], queryFn: () => get<Visao[]>('/visoes', { tela }) });
  const [visaoAtiva, setVisaoAtiva] = useState<string | null>(null);
  const [aplicouPadrao, setAplicouPadrao] = useState(false);
  // Valores da tela ao abrir: base do que a visão não traz e do "sem visão".
  const [filtrosIniciais] = useState<FiltrosTabela>(() => filtros ?? {});
  function aplicar(v: Visao, o: { manterBusca?: boolean; manterLink?: boolean } = {}) {
    // Review r26 robustez (M2): a visão grande demais (ou que levaria a URL além do teto) é ignorada com um aviso, sem
    // travar a lista (antes: 431 em toda abertura com ela como padrão).
    if (!visaoCabe(v, filtrosIniciais, new URLSearchParams(window.location.search))) {
      toast.info(t('tabela.visaoIgnorada', { nome: v.nome }));
      // Escolhida no seletor: fica marcada (sem filtro nenhum aplicado) para o botão de remover aparecer.
      if (!o.manterLink) setVisaoAtiva(v.id);
      return;
    }
    setVisaoAtiva(v.id);
    // Os filtros da tela antes da busca: a tela grava os dela na URL e a busca entra por cima (B4, r24 spec).
    // Review r25 spec (M1): na visão padrão aplicada sozinha ao abrir, o que veio preenchido no link prevalece.
    aoAplicarFiltros?.(o.manterLink ? filtrosDaVisaoPadrao(v.filtros, filtros ?? filtrosIniciais, new URLSearchParams(window.location.search)) : filtrosDaVisao(v.filtros, filtrosIniciais));
    if (!o.manterBusca) setBusca(String(v.filtros?.busca ?? ''));
    setOrdem(v.ordenacao ?? {});
    setVisiveis(v.colunas.length ? v.colunas : null);
  }
  useEffect(() => {
    if (aplicouPadrao || !visoes.data) return;
    // E1 (R37-SPEC-M2): o link de uma visão (Relatórios > visão de lista) traz `?visao_salva=<id>`: abre com ela, no lugar
    // da padrão, e o parâmetro sai da URL (replace). A que a pessoa não enxerga (ou não existe mais) é ignorada.
    const pedida = url.get(PARAMETRO_VISAO_SALVA);
    if (pedida !== null) {
      const proxima = new URLSearchParams(window.location.search);
      proxima.delete(PARAMETRO_VISAO_SALVA);
      proxima.delete(PARAMETRO_TELA_DA_VISAO);
      setUrl(proxima, { replace: true });
      const v = visoes.data.find((x) => x.id === pedida);
      if (v) {
        aplicar(v);
        setAplicouPadrao(true);
        return;
      }
    }
    const p = visoes.data.find((v) => v.padrao);
    // Review r24 spec (B4): quem chegou pelo link com `?busca=` vê o documento do link, não a busca da visão padrão.
    if (p) aplicar(p, { manterBusca: buscaNaUrl && !!buscaDaUrl(url), manterLink: true });
    setAplicouPadrao(true);
  }, [visoes.data, aplicouPadrao]);

  const consulta = useInfiniteQuery({
    queryKey: [...(chaveConsulta ?? [caminho]), params, buscaD, ordem],
    queryFn: ({ pageParam }) => get<Pagina<T>>(caminho, { ...params, busca: buscaD || undefined, cursor: pageParam, ...ordem }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (p) => p.proximo_cursor ?? undefined,
  });
  const linhas = useMemo(() => consulta.data?.pages.flatMap((p) => p.itens) ?? [], [consulta.data]);
  // B8 (r5 robustez): linhas em blocos; só os perto da janela ficam no DOM (ver `tabela-virtual.tsx`). Blocos estáveis por
  // página (r6 robustez B1): a página já carregada devolve os mesmos arrays e o memo do bloco segura o render.
  const [cacheBlocos] = useState(() => new WeakMap<object, T[][]>());
  const blocos = useMemo(() => blocosPorPagina(consulta.data?.pages ?? [], cacheBlocos), [consulta.data, cacheBlocos]);
  const refRaiz = useRef<HTMLDivElement>(null);
  const blocoComFoco = useBlocoComFoco(refRaiz);
  const desktop = useDesktop();
  const imprimindo = useImprimindo();
  const { media, registrar } = useMediaDeLinha();
  const refTabela = useRef<HTMLTableElement>(null);
  const refSeletorVisoes = useRef<HTMLSelectElement>(null);
  const refRolagem = useRef<HTMLDivElement>(null);
  const rolagemSemFoco = useRolagemSemFoco(refRolagem, `${colunasMostradas.map((c) => c.chave).join('|')}|${linhas.length}|${desktop}`);
  const larguras = useLargurasEstaveis(refTabela, colunasMostradas.map((c) => c.chave).join('|') + `|${linhas.length > 0}|${desktop}|${!!selecao}`);
  const desloc = selecao ? 1 : 0;
  const escolhiveis = useMemo(() => (selecao ? linhas.filter((l) => selecao.pode?.(l) ?? true) : []), [linhas, selecao]);
  const marcadasCarregadas = selecao ? escolhiveis.filter((l) => selecao.marcados.has(l.id)).length : 0;
  const temMais = !!consulta.hasNextPage;

  const [modalVisao, setModalVisao] = useState(false);
  const [nomeVisao, setNomeVisao] = useState('');
  const [padrao, setPadrao] = useState(false);
  const podeCompartilhar = usePodeCompartilharVisao();
  const [compartilhar, setCompartilhar] = useState<ValorCompartilhar>(SEM_COMPARTILHAR);
  const salvarVisao = useMutation({
    mutationFn: () =>
      post<{ id: string }>('/visoes', { tela, nome: nomeVisao, filtros: filtrosParaVisao(busca, filtros), colunas: colunasMostradas.map((c) => c.chave), ordenacao: ordem, padrao, ...corpoCompartilhar(compartilhar, podeCompartilhar) }),
    onSuccess: async (r) => {
      setModalVisao(false);
      setNomeVisao('');
      setCompartilhar(SEM_COMPARTILHAR);
      setVisaoAtiva(r.id);
      await qc.invalidateQueries({ queryKey: ['visoes', tela] });
      toast.sucesso(t('comum.salvo'));
    },
    // Review r27 spec (M1): o 422 (`visao.limite`, tetos de tamanho) e o conflito de padrão chegam à pessoa; antes, o diálogo
    // ficava parado sem nenhum aviso (o QueryClient não trata erro de mutação).
    onError: (e) => toast.erro(mensagemDoErroDaVisao(e)),
  });
  // E1 (R37-SPEC-M2): editar a visão própria ativa (nome, com quem está compartilhada e "Mostrar em Relatórios").
  const [editando, setEditando] = useState<{ id: string; nome: string; valor: ValorCompartilhar } | null>(null);
  const editarVisao = useMutation({
    mutationFn: (e: { id: string; nome: string; valor: ValorCompartilhar }) => patch(`/visoes/${e.id}`, { nome: e.nome, ...corpoCompartilhar(e.valor, podeCompartilhar) }),
    onSuccess: async () => {
      setEditando(null);
      await qc.invalidateQueries({ queryKey: ['visoes', tela] });
      toast.sucesso(t('comum.salvo'));
    },
    onError: (e) => toast.erro(mensagemDoErroDaVisao(e)),
  });
  const ativa = visoes.data?.find((v) => v.id === visaoAtiva);
  const ativaPropria = !!visaoAtiva && ativa?.meu !== false;
  const removerVisao = useMutation({
    mutationFn: (id: string) => del(`/visoes/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['visoes', tela] }),
    onError: (e) => toast.erro(mensagemDoErroDaVisao(e)),
  });

  // A2 (r6 visual): a célula principal leva o botão que abre o registro (como o cartão do celular); o clique na linha continua
  // valendo para o mouse. O bloco com o foco dentro nunca é desmontado (tabela-virtual), então o Tab segue pelas linhas.
  const principalDesktop = colunasMostradas.find((c) => c.principal) ?? colunasMostradas[0];
  const tamanhoConjunto = temMais ? -1 : linhas.length;

  // Render das linhas de um bloco, chamado só com o bloco montado (r6 robustez B1). Estável enquanto colunas e clique não mudam:
  // digitar na busca ou carregar mais não renderiza de novo os blocos já montados. A borda fica em cima (menos na primeira linha)
  // para não depender do total de linhas carregadas.
  const linhasDaTabela = useCallback(
    (bloco: T[], inicio: number) =>
      bloco.map((l, i) => {
        const n = inicio + i;
        return (
          <tr key={l.id} aria-rowindex={n + 2} onClick={aoClicar ? () => aoClicar(l) : undefined} className={`${n === 0 ? '' : 'border-t border-borda'} ${aoClicar ? 'cursor-pointer hover:bg-sup-2' : ''}`}>
            {selecao && (
              <td className="w-10 px-3 py-2.5" data-sem-impressao>
                <CaixaSelecao marcado={selecao.marcados.has(l.id)} rotulo={selecao.rotulo(l)} desabilitado={!(selecao.pode?.(l) ?? true)} aoMudar={(v) => selecao.aoAlternar(l, v)} />
              </td>
            )}
            {colunasMostradas.map((c) => {
              const conteudo = celula(c, l);
              return (
                <td key={c.chave} className={`px-4 py-3 ${c.alinhar === 'direita' ? 'text-right' : ''} ${c.mono ? 'numero whitespace-nowrap' : ''}`}>
                  {aoClicar && c === principalDesktop ? <BotaoAbrirLinha aoAbrir={() => aoClicar(l)}>{anunciarOcultos(conteudo)}</BotaoAbrirLinha> : anunciarOcultos(conteudo)}
                </td>
              );
            })}
          </tr>
        );
      }),
    [aoClicar, colunasMostradas, principalDesktop, selecao],
  );
  const acoesNoCartao = useMemo(() => colunasMostradas.filter((c) => c.acaoNoCartao), [colunasMostradas]);
  const cartoes = useCallback(
    (bloco: T[], inicio: number) => {
      const principal = principalDesktop;
      return bloco.map((l, i) => {
        const n = inicio + i;
        const valor = principal ? celula(principal, l) : valorCru(l, 'id');
        return (
          <div key={l.id} role="listitem" aria-posinset={n + 1} aria-setsize={tamanhoConjunto} className={`${n === 0 ? '' : 'border-t border-borda'} ${selecao || acoesNoCartao.length ? 'flex min-w-0 items-start' : ''}`}>
            {selecao && (
              <span className="shrink-0 py-2.5 pl-3" data-sem-impressao>
                <CaixaSelecao marcado={selecao.marcados.has(l.id)} rotulo={selecao.rotulo(l)} desabilitado={!(selecao.pode?.(l) ?? true)} aoMudar={(v) => selecao.aoAlternar(l, v)} />
              </span>
            )}
            <button type="button" disabled={!aoClicar} onClick={() => aoClicar?.(l)} className="flex w-full min-w-0 flex-col gap-1 px-4 py-3 text-left enabled:hover:bg-sup-2">
              <span className="truncate font-medium" title={textoDoNo(valor) || undefined}>
                {anunciarOcultos(valor)}
              </span>
              {colunasMostradas
                .filter((c) => c !== principal && !c.acaoNoCartao)
                .slice(0, 3)
                .map((c) => {
                  const v = celula(c, l);
                  return (
                    <span key={c.chave} className="flex min-w-0 justify-between gap-3 text-xs text-tinta-3">
                      <span className="shrink-0">{c.rotulo}</span>
                      <span className={`min-w-0 truncate text-right text-tinta-2 ${c.mono ? 'numero' : ''}`} title={textoDoNo(v) || undefined}>
                        {anunciarOcultos(v)}
                      </span>
                    </span>
                  );
                })}
            </button>
            {acoesNoCartao.map((c) => (
              <span key={c.chave} className="shrink-0 py-2.5 pr-3" data-acao-cartao={c.chave} data-sem-impressao>
                {celula(c, l)}
              </span>
            ))}
          </div>
        );
      });
    },
    [aoClicar, colunasMostradas, principalDesktop, tamanhoConjunto, selecao, acoesNoCartao],
  );

  function alternarOrdem(c: Coluna<T>) {
    if (!c.ordenavel) return;
    setOrdem((o) => (o.ordem !== c.chave ? { ordem: c.chave, direcao: 'asc' } : o.direcao === 'asc' ? { ordem: c.chave, direcao: 'desc' } : {}));
  }

  return (
    <div ref={refRaiz} className="min-w-0 rounded-xl border border-borda bg-sup-1 shadow-cartao">
      <div className="flex flex-wrap items-center gap-2 border-b border-borda p-3" data-sem-impressao>
        {/* U15 F2: alvo da dica "aqui você procura" (a busca da lista). */}
        <div className="relative min-w-0 flex-1 basis-56" data-tour="filtro">
          <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-tinta-3" aria-hidden />
          <Entrada aria-label={t('comum.buscar')} placeholder={t('comum.buscar')} className="pl-9" value={busca} maxLength={TETO_BUSCA} onChange={(e) => setBusca(e.target.value)} data-busca-tabela="" />
        </div>
        {filtrosExtra}
        <div className="flex items-center gap-1">
          <select
            ref={refSeletorVisoes}
            aria-label={t('tabela.visoes')}
            className="h-11 max-w-44 rounded-lg border border-borda-campo bg-campo px-2 text-base text-tinta-2 md:h-10 md:text-sm"
            value={visaoAtiva ?? ''}
            onChange={(e) => {
              const v = visoes.data?.find((x) => x.id === e.target.value);
              if (v) aplicar(v);
              else {
                setVisaoAtiva(null);
                setVisiveis(null);
                setOrdem({});
                aoAplicarFiltros?.(filtrosIniciais);
                setBusca('');
              }
            }}
          >
            <option value="">{t('tabela.visoes')}</option>
            {visoes.data?.map((v) => (
              <option key={v.id} value={v.id}>
                {v.meu === false && v.autor ? t('tabela.visaoDe', { nome: v.nome, autor: nomePessoa(v.autor) }) : v.nome}
              </option>
            ))}
          </select>
          {visaoAtiva && ativaPropria && ativa && (podeCompartilhar.compartilhar || podeCompartilhar.relatorios) && (
            <Botao
              variante="fantasma"
              tamanho="sm"
              aria-label={t('tabela.editarVisao')}
              title={t('tabela.editarVisao')}
              onClick={() =>
                setEditando({
                  id: ativa.id,
                  nome: ativa.nome,
                  valor: { compartilhamento: ativa.compartilhamento ?? 'privado', papeis: ativa.papeis ?? [], pessoas: ativa.pessoas ?? [], em_relatorios: !!ativa.em_relatorios },
                })
              }
              icone={<Pencil className="size-4" />}
            />
          )}
          {visaoAtiva && ativaPropria && (
            <Botao variante="fantasma" tamanho="sm" aria-label={t('tabela.removerVisao')} onClick={(e) => {
                const botao = e.currentTarget;
                removerVisao.mutate(visaoAtiva, {
                  onSuccess: () => {
                    devolverFocoAoSeletorDeVisoes({ ativo: document.activeElement, botao, corpo: document.body, seletor: refSeletorVisoes.current });
                    setVisaoAtiva(null);
                  },
                });
              }}
              icone={<Trash2 className="size-4" />} />
          )}
          <Botao variante="fantasma" tamanho="sm" aria-label={t('tabela.salvarVisao')} title={t('tabela.salvarVisao')} onClick={() => setModalVisao(true)} icone={<Bookmark className="size-4" />} />
          <Popover rotulo={t('tabela.colunas')} icone={<Columns3 className="size-4" />}>
            {todasColunas.map((c) => {
              const marcada = colunasMostradas.some((x) => x.chave === c.chave);
              return (
                <label key={c.chave} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-sup-3">
                  <input
                    type="checkbox"
                    checked={marcada}
                    onChange={() => {
                      const atual = colunasMostradas.map((x) => x.chave);
                      setVisiveis(marcada ? atual.filter((k) => k !== c.chave) : todasColunas.map((x) => x.chave).filter((k) => atual.includes(k) || k === c.chave));
                    }}
                  />
                  <span className="min-w-0 truncate" title={c.rotulo || t('comum.acoes')}>
                    {c.rotulo || t('comum.acoes')}
                  </span>
                </label>
              );
            })}
          </Popover>
        </div>
      </div>

      {consulta.isPending ? (
        <Carregando />
      ) : consulta.isError ? (
        <Erro erro={consulta.error} aoTentar={() => consulta.refetch()} />
      ) : mostraEstadoVazio(linhas.length, temMais) ? (
        buscaD ? <Vazio titulo={t('estados.semResultado')} texto="" /> : (vazio ?? <Vazio />)
      ) : (
        <>
          {/* Tabela no desktop (na impressão entram as duas versões e o CSS escolhe). Review r17 spec (M2): os th são
              relative para o sr-only (absoluto) do rótulo das ações ficar recortado pela rolagem em tabela larga. */}
          {(desktop || imprimindo) && (
            <div
              ref={refRolagem}
              className="hidden overflow-x-auto md:block"
              data-impressao="tabela"
              {...(rolagemSemFoco ? { tabIndex: 0, role: 'region', 'aria-label': t('comum.tabelaRolavel') } : {})}
            >
              <table ref={refTabela} className="w-full text-sm" aria-rowcount={totalDeLinhasAria(linhas.length, temMais)}>
                <thead>
                  <tr aria-rowindex={1} className="border-b border-borda bg-sup-2 text-left">
                    {selecao && (
                      <th scope="col" className="relative w-10 px-3 py-1.5" data-sem-impressao>
                        {selecao.aoAlternarTodas ? (
                          <CaixaSelecao
                            marcado={escolhiveis.length > 0 && marcadasCarregadas === escolhiveis.length}
                            indeterminado={marcadasCarregadas > 0 && marcadasCarregadas < escolhiveis.length}
                            desabilitado={escolhiveis.length === 0}
                            rotulo={selecao.rotuloTodas ?? t('comum.acoes')}
                            aoMudar={(v) => selecao.aoAlternarTodas!(escolhiveis, v)}
                          />
                        ) : (
                          <span className="sr-only">{selecao.rotuloTodas ?? t('comum.acoes')}</span>
                        )}
                      </th>
                    )}
                    {colunasMostradas.map((c, i) => (
                      <th
                        key={c.chave}
                        scope="col"
                        aria-sort={ordem.ordem === c.chave ? (ordem.direcao === 'asc' ? 'ascending' : 'descending') : undefined}
                        style={larguras[i + desloc] ? { minWidth: larguras[i + desloc] } : undefined}
                        className={`relative h-11 px-4 py-2 text-xs font-semibold tracking-wider whitespace-nowrap text-tinta-3 uppercase ${c.alinhar === 'direita' ? 'text-right' : ''}`}
                      >
                        {c.ordenavel ? (
                          <button type="button" onClick={() => alternarOrdem(c)} className="inline-flex items-center gap-1 uppercase hover:text-tinta-1">
                            {c.rotulo}
                            {ordem.ordem === c.chave && (ordem.direcao === 'asc' ? <ArrowUp className="size-3.5" /> : <ArrowDown className="size-3.5" />)}
                          </button>
                        ) : (
                          // r8 visual B9: coluna sem rótulo visível (ações) tem nome para o leitor de tela (empty-table-header).
                          c.rotulo || <span className="sr-only">{t('comum.acoes')}</span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                {blocos.map((bloco, b) => (
                  <BlocoVirtual
                    key={bloco.chave}
                    indice={b}
                    linhas={bloco.linhas}
                    inicio={bloco.inicio}
                    tag="tbody"
                    colunas={colunasMostradas.length + desloc}
                    forcar={imprimindo}
                    pertoDoFoco={perto(b, blocoComFoco)}
                    media={media}
                    aoMedir={registrar}
                    renderizar={linhasDaTabela}
                  />
                ))}
              </table>
            </div>
          )}
          {/* Cartões no celular */}
          {(!desktop || imprimindo) && (
            <div role="list" className="md:hidden" data-impressao="cartoes">
              {blocos.map((bloco, b) => (
                <BlocoVirtual
                  key={bloco.chave}
                  indice={b}
                  linhas={bloco.linhas}
                  inicio={bloco.inicio}
                  tag="div"
                  forcar={imprimindo}
                  pertoDoFoco={perto(b, blocoComFoco)}
                  media={media}
                  aoMedir={registrar}
                  renderizar={cartoes}
                />
              ))}
            </div>
          )}
          {/* Impressão (r6 robustez B2): só as linhas carregadas saem; o aviso diz isso quando há mais no servidor. */}
          {temMais && <p className="hidden px-4 py-2 text-xs text-tinta-2 print:block">{t('tabela.impressaoParcial', { count: linhas.length })}</p>}
          {consulta.hasNextPage && (
            <div className="flex justify-center border-t border-borda p-3" data-sem-impressao>
              <Botao variante="fantasma" carregando={consulta.isFetchingNextPage} onClick={(e) => carregarMais(() => consulta.fetchNextPage(), e.currentTarget, { itens: () => itensVisiveisDaTabela(refRaiz.current) })}>
                {t('comum.carregarMais')}
              </Botao>
            </div>
          )}
        </>
      )}

      <Modal
        aberto={modalVisao}
        aoFechar={() => setModalVisao(false)}
        titulo={t('tabela.salvarVisao')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => setModalVisao(false)}>
              {t('comum.cancelar')}
            </Botao>
            <Botao variante="primario" disabled={!nomeVisao.trim()} carregando={salvarVisao.isPending} onClick={() => salvarVisao.mutate()}>
              {t('comum.salvar')}
            </Botao>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <label className="flex flex-col gap-1.5 text-sm font-medium text-tinta-2">
            {t('tabela.nomeVisao')}
            <Entrada value={nomeVisao} onChange={(e) => setNomeVisao(e.target.value)} maxLength={80} />
          </label>
          <label className="flex items-center gap-2 text-sm text-tinta-2">
            <input type="checkbox" checked={padrao} onChange={(e) => setPadrao(e.target.checked)} />
            {t('tabela.padrao')}
          </label>
          {modalVisao && <CamposCompartilharVisao valor={compartilhar} aoMudar={setCompartilhar} />}
        </div>
      </Modal>

      <Modal
        aberto={!!editando}
        aoFechar={() => setEditando(null)}
        titulo={t('tabela.editarVisao')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => setEditando(null)}>
              {t('comum.cancelar')}
            </Botao>
            <Botao variante="primario" disabled={!editando?.nome.trim()} carregando={editarVisao.isPending} onClick={() => editando && editarVisao.mutate(editando)}>
              {t('comum.salvar')}
            </Botao>
          </>
        }
      >
        {editando && (
          <div className="flex flex-col gap-4">
            <label className="flex flex-col gap-1.5 text-sm font-medium text-tinta-2">
              {t('tabela.nomeVisao')}
              <Entrada value={editando.nome} onChange={(e) => setEditando({ ...editando, nome: e.target.value })} maxLength={80} />
            </label>
            <CamposCompartilharVisao valor={editando.valor} aoMudar={(valor) => setEditando({ ...editando, valor })} />
          </div>
        )}
      </Modal>
    </div>
  );
}
